import React from "react";
import { Box, Typography, CardContent, useTheme, Theme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { InventoryMovementBiDto } from "../../../shared/dtos/bi/InventoryMovementBiDto";

interface InventoryMovementWidgetProps {
  data?: InventoryMovementBiDto[];
}

const getBarColor = (type: string, theme: Theme): string => {
  const upperType = type.toUpperCase();
  if (upperType.includes("SALE") || upperType.includes("OUT"))
    return theme.palette.success.main;
  if (upperType.includes("RECEIPT") || upperType.includes("IN"))
    return theme.palette.info.main;
  if (upperType.includes("DAMAGE") || upperType.includes("LOSS"))
    return theme.palette.error.main;
  return theme.palette.warning.main;
};

const formatMovementType = (type: string): string => {
  if (!type) return "";
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const InventoryMovementWidget: React.FC<InventoryMovementWidgetProps> = ({
  data = [],
}) => {
  const theme = useTheme();

  const formatType = (type: unknown): string => {
    if (typeof type !== "string") return "";
    return type
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Inventory Movements (Qty)
        </Typography>

        {data.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="movementType"
                  tickFormatter={formatMovementType}
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(value)
                  }
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                />
                <RechartsTooltip
                  cursor={{ fill: "rgba(0,0,0,0.05)" }}
                  contentStyle={{
                    borderRadius: 8,
                    backgroundColor: theme.palette.background.paper,
                  }}
                  formatter={(val) => [val, "Total Quantity"]}
                  labelFormatter={formatType}
                />
                <Bar dataKey="totalQuantity" radius={[4, 4, 0, 0]} barSize={50}>
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.movementType, theme)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 300,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No inventory movements recorded.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default InventoryMovementWidget;
